import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"
import { FormFieldRendererMixin, TemplateEditorMixin, applyMixin } from "../lib/form_template_editor"

// Company settings → Construction → Form Templates (Phelipe 2026-09-09):
// the same "+ New type" pop-up, AI import and editor the Reports page has,
// running in two modes:
//   import  — the list page: name + PDF → editor → Save creates the form
//             template AND a matching report type; the new row is added in
//             place.
//   builder — the template page: the editor fills the page, Save writes the
//             schema back; the name is renamed in place from the header.
// Everything the two modes share lives in lib/form_template_editor.
class TemplateEditorController extends Controller {
  static targets = [
    // "+ New template" pop-up
    "newTypeModal", "newTypeName", "newTypeError", "newTypeSubmit",
    "newTypeDrop", "newTypeFile", "newTypeAiStatus",
    // Editor (pop-up on the list page, inline on the builder page)
    "templatePreviewModal", "templatePreviewBody", "templatePreviewName",
    "templatePreviewSave", "templatePreviewError", "templateInspector",
    "templatePreviewConflict", "templatePreviewConflictName",
    // List page: where the new row goes
    "listBody", "rowTemplate", "emptyRow",
    // Builder page: name rename + save state
    "nameView", "nameForm", "nameInput", "nameSaveBtn", "saveBtn", "dirtyNote"
  ]

  static values = {
    mode: { type: String, default: "import" },
    presignUrl: String,
    extractTemplateUrl: String,
    createTypeUrl: String,
    updateUrl: String,
    builderUrlPattern: String,
    schema: Object,
    signIconHtml: String
  }

  connect() {
    if (this.modeValue !== "builder") return
    this._templateDraft = JSON.parse(JSON.stringify(this.schemaValue || { sections: [] }))
    if (!Array.isArray(this._templateDraft.sections)) this._templateDraft.sections = []
    this._tpSelection = null
    this._dirty = false
    this._renderTemplateFormPane()
    this._renderTemplateInspector()
    this._onBeforeUnload = (e) => { if (this._dirty) { e.preventDefault(); e.returnValue = "" } }
    window.addEventListener("beforeunload", this._onBeforeUnload)
  }

  disconnect() {
    if (this._onBeforeUnload) window.removeEventListener("beforeunload", this._onBeforeUnload)
  }

  // ── Hooks the shared editor calls ─────────────────────────────────

  // Every edit on the builder page lights up Save.
  _templateChanged() {
    if (this.modeValue !== "builder") return
    this._dirty = true
    if (this.hasDirtyNoteTarget) this.dirtyNoteTarget.classList.remove("hidden")
  }

  // The settings list creates the pair — type + template — even without a
  // PDF; the report form's plain "+ New type" keeps creating a type only.
  _newTypeExtraParams() {
    return { with_template: "1" }
  }

  // Import mode: the new template gets a row at the top of the list, no
  // reload. A replaced form ("Update its form") already has its row.
  _onTemplateTypeSaved(data) {
    this.closeTemplatePreview()
    this.closeNewTypeModal()
    const templateId = data.form_template_id
    if (templateId && this.hasListBodyTarget && this.hasRowTemplateTarget &&
        !this.listBodyTarget.querySelector(`tr[data-id="${templateId}"]`)) {
      const name = data.form_template_name || data.name || ""
      const desc = data.form_template_description || ""
      const url = (this.builderUrlPatternValue || "").replace(":id", String(templateId))
      const html = this.rowTemplateTarget.innerHTML
        .replace(/__ID__/g, String(templateId))
        .replace(/__NAME_ATTR__/g, this._escAttr(name))
        .replace(/__DESC_ATTR__/g, this._escAttr(desc))
        .replace(/__NAME__/g, this._escHtml(name))
        .replace(/__DESC__/g, desc ? this._escHtml(desc) : "—")
        .replace(/__URL__/g, this._escAttr(url))
      if (this.hasEmptyRowTarget) this.emptyRowTarget.remove()
      this.listBodyTarget.insertAdjacentHTML("afterbegin", html)
    }
    this._flashToast(data.replaced
      ? `Updated the form for “${data.name}”.`
      : `Template “${data.name}” is ready — it's also a report type now.`)
    track("Report Template Created", {
      source: "settings", with_form: !!data.form_template_schema, replaced: !!data.replaced
    })
  }

  // ── Builder page: save the schema ──────────────────────────────────

  async saveTemplate() {
    const finalized = this._finalizeTemplateDraft()
    const btn = this.hasSaveBtnTarget ? this.saveBtnTarget : null
    if (btn) { btn.disabled = true }
    try {
      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this._csrfToken() },
        body: JSON.stringify({ form_template: { schema: finalized } })
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        this._showToast(data.errors?.join(", ") || "Could not save the form", "error")
        return
      }
      this._dirty = false
      if (this.hasDirtyNoteTarget) this.dirtyNoteTarget.classList.add("hidden")
      // Keep the draft's keys in step with what was saved.
      this._templateDraft = JSON.parse(JSON.stringify(finalized))
      this._renderTemplateFormPane()
      this._renderTemplateInspector()
      this._showToast(data.message || "Form saved")
      track("Report Template Saved", { template_id: this._templateId(), sections: finalized.sections.length })
    } catch {
      this._showToast("Network error", "error")
    } finally {
      if (btn) btn.disabled = false
    }
  }

  // ── Builder page: template name, edited in place ──────────────────

  startRename() {
    this.nameInputTarget.value = this._currentName()
    this.nameViewTarget.classList.add("hidden")
    this.nameFormTarget.classList.remove("hidden")
    this.nameFormTarget.classList.add("flex")
    this.nameInputTarget.focus()
    this.nameInputTarget.select()
  }

  cancelRename() {
    this.nameFormTarget.classList.add("hidden")
    this.nameFormTarget.classList.remove("flex")
    this.nameViewTarget.classList.remove("hidden")
  }

  renameKeydown(e) {
    if (e.key === "Escape") { e.preventDefault(); this.cancelRename() }
  }

  async saveRename(e) {
    e.preventDefault()
    const name = this.nameInputTarget.value.trim()
    if (!name) { this._showToast("Give the template a name", "error"); return }
    const current = this._currentName()
    if (name === current) { this.cancelRename(); return }

    this.nameSaveBtnTarget.disabled = true
    try {
      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this._csrfToken() },
        body: JSON.stringify({ form_template: { name } })
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        this._showToast(data.errors?.join(", ") || "Could not rename the template", "error")
        return
      }
      const saved = data.name || name
      this.element.querySelectorAll(".js-template-name").forEach(el => { el.textContent = saved })
      document.title = document.title.replace(current, saved)
      this.cancelRename()
      this._showToast(data.message || "Template renamed")
      track("Report Template Renamed", { template_id: this._templateId() })
    } catch {
      this._showToast("Network error", "error")
    } finally {
      this.nameSaveBtnTarget.disabled = false
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────

  _currentName() {
    return this.element.querySelector(".js-template-name")?.textContent.trim() || ""
  }

  _templateId() {
    return Number((this.updateUrlValue || "").split("/").pop()) || null
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}

applyMixin(TemplateEditorController, FormFieldRendererMixin)
applyMixin(TemplateEditorController, TemplateEditorMixin)

export default TemplateEditorController
